import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Clock3, IndianRupee } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { WHATSAPP_URL } from "../data/content";

export const FAQS = [
  {
    q: "What is a Bronchoscopy and why might I need one?",
    a: "Bronchoscopy is a day-care procedure where a thin, flexible camera (bronchoscope) is gently passed through the nose or mouth into your airways. It lets Dr. Singh directly see the windpipe and lung passages to find the cause of a persistent cough, bleeding (hemoptysis), a shadow on the chest X-ray, infection, or a suspected tumour — and take biopsies or clear mucus plugs and foreign bodies in the same sitting. It is done under local anaesthesia with mild sedation, takes 15–30 minutes, and most patients go home the same day.",
    prep: "NPO (nothing to eat or drink) × 4 hours before the procedure",
    cost: "₹10,000 – ₹50,000",
  },
  {
    q: "What is EBUS (Endobronchial Ultrasound)?",
    a: "EBUS is an advanced bronchoscope with an ultrasound probe at its tip, allowing Dr. Singh to see — and needle-biopsy — lymph nodes and masses hiding behind the airway walls, without a single surgical cut. It is the gold standard for lung cancer staging and for diagnosing TB lymph nodes and sarcoidosis. Done under sedation as a day-care procedure, it often replaces far more invasive surgeries like mediastinoscopy.",
    prep: "NPO (nothing to eat or drink) × 4 hours before the procedure",
    cost: "₹20,000 – ₹50,000",
  },
  {
    q: "What is Medical Thoracoscopy (Pleuroscopy)?",
    a: "Medical thoracoscopy is a minimally invasive procedure where a small camera is passed into the pleural space (the area around the lungs) through a tiny chest opening under local anaesthesia. It gives a definitive answer for unexplained fluid around the lungs (pleural effusion), allows direct pleural biopsies, and enables talc pleurodesis to stop fluid from coming back — all in one sitting.",
    prep: "NPO (nothing to eat or drink) × 4 hours before the procedure",
    cost: "₹20,000 – ₹50,000",
  },
  {
    q: "What is Rigid Thoracoscopy and when is it needed?",
    a: "Rigid thoracoscopy uses a firm, wide-bore scope instead of a flexible one, giving a stronger working channel into the pleural space. It is preferred when larger pleural biopsies are needed, when thick fluid or adhesions must be broken down, or when a complex pleurodesis is planned. It is performed under sedation or short anaesthesia with the same small-incision, fast-recovery approach.",
    prep: "NPO (nothing to eat or drink) × 4 hours before the procedure",
    cost: "₹20,000 – ₹50,000",
  },
  {
    q: "What is Rigid Bronchoscopy?",
    a: "Rigid bronchoscopy uses a hollow metal tube placed into the windpipe under general anaesthesia. It is the safest and most powerful way to handle big airway problems — removing foreign bodies, controlling massive bleeding (hemoptysis), opening up tumours blocking the central airways, and placing airway stents. Patients sleep through it and typically recover quickly.",
    prep: "NPO (nothing to eat or drink) × 4 hours before the procedure",
    cost: "₹40,000 – ₹70,000",
  },
  {
    q: "What is Airway Tumour Debulking?",
    a: "When a tumour grows inside the airway and chokes off breathing, debulking removes it from the inside — usually through a rigid bronchoscope using heat (laser/APC), freezing (cryotherapy), or mechanical coring. Patients who arrive breathless often walk out breathing comfortably the same day. It can be life-saving and buys crucial time for cancer treatment to work.",
    prep: "NPO (nothing to eat or drink) × 4 hours before the procedure",
    cost: "₹40,000 – ₹80,000",
  },
  {
    q: "What are TBLC and TBLB lung biopsies?",
    a: "TBLB (transbronchial lung biopsy) and TBLC (transbronchial lung cryobiopsy) sample lung tissue through a bronchoscope — no surgical cuts. TBLC uses a freezing probe to collect larger, high-quality pieces of lung, which is often decisive in diagnosing interstitial lung disease (ILD) and can avoid open surgical lung biopsy altogether. Both are done under sedation with careful monitoring.",
    prep: "NPO (nothing to eat or drink) × 4 hours before the procedure",
    cost: "₹30,000 – ₹50,000",
  },
  {
    q: "What does ICU & Critical Care include, and what does it cost per day?",
    a: "Critical care is round-the-clock, protocol-driven care for the sickest patients — mechanical ventilation for lungs that can't breathe on their own, ARDS and sepsis management, ECMO-level support when needed, continuous monitoring, and daily family counselling so you always know the plan. Dr. Singh has managed thousands of ICU admissions across AIIMS Jodhpur, AIIMS Bathinda and GMCH Chandigarh.",
    prep: "Not applicable — ICU admission is based on medical need",
    cost: "₹10,000 – ₹30,000 per day",
  },
  {
    q: "What is ILD, and what tests and treatments do you offer for it?",
    a: "Interstitial Lung Disease (ILD) is a group of disorders — including IPF, hypersensitivity pneumonitis, sarcoidosis and arthritis-related lung disease — that cause scarring and stiffening of the lungs, leading to progressive breathlessness and dry cough. Dr. Singh offers complete ILD care: PFT (pulmonary function tests / spirometry) to measure lung capacity, DLCO to measure how well oxygen passes into the blood, HRCT interpretation, 6-minute walk testing, antifibrotic medication, oxygen therapy and structured pulmonary rehabilitation.",
    prep: "PFT and DLCO need no fasting — just avoid smoking and heavy inhaler use before the test as advised",
    cost: "Discussed at consultation",
  },
  {
    q: "What is a Sleep Study (PSG), and do I have Sleep Apnea?",
    a: "Obstructive Sleep Apnea (OSA) shows up as loud snoring, choking at night, unrefreshing sleep and heavy daytime sleepiness — and silently raises blood pressure, sugar and heart risk. A Level-1 Polysomnography (PSG), the gold-standard in-lab sleep study available with Dr. Singh, records your brain waves, breathing, oxygen and heart rhythm overnight to confirm OSA precisely. If positive, CPAP/BiPAP titration finds the exact pressure that keeps your airway open — most patients feel transformed within days.",
    prep: "Sleep study is overnight and painless — simply avoid caffeine and afternoon naps on the study day",
    cost: "Discussed at consultation",
  },
];

const FaqItem = ({ item, open, onToggle, index }) => (
  <div className="border border-[#E3DCCB] rounded-2xl bg-[#FFFFFF]/50 overflow-hidden hover:border-teal-500/30 transition-colors duration-300"
    data-testid={`faq-item-${index + 1}`}>
    <button onClick={onToggle} aria-expanded={open} data-testid={`faq-toggle-${index + 1}`}
      className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-6 text-left">
      <h3 className="font-serif-display text-lg sm:text-xl font-semibold text-stone-800 leading-snug">{item.q}</h3>
      <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }}
        className="shrink-0 w-8 h-8 rounded-full border border-teal-500/40 flex items-center justify-center text-teal-700">
        <Plus size={15} />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
          <div className="px-6 sm:px-8 pb-7">
            <p className="text-sm sm:text-base text-stone-500 leading-relaxed max-w-4xl">{item.a}</p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-teal-500/30 bg-teal-500/10 text-teal-700 text-xs font-medium">
                <Clock3 size={13} /> {item.prep}
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-700 text-xs font-medium"
                data-testid={`faq-cost-${index + 1}`}>
                <IndianRupee size={13} /> {item.cost}
              </span>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
                className="text-xs font-medium text-teal-700 hover:text-teal-800 underline underline-offset-4 decoration-teal-500/40">
                Ask about this on WhatsApp →
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Faq = () => {
  const [open, setOpen] = useState(0);
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: `${f.a} Preparation: ${f.prep}. Cost: ${f.cost}.` },
    })),
  };
  return (
    <section id="faq" className="py-28 sm:py-36" data-testid="faq-section">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Overline>Patient Guide — Procedures, Preparation & Costs</Overline>
          <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-stone-800 max-w-3xl leading-snug">
            Every question, <span className="text-teal-700 italic">answered honestly.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-stone-500 max-w-2xl leading-relaxed">
            Transparent answers on bronchoscopy, EBUS, thoracoscopy, ICU care, ILD testing and sleep studies —
            including how to prepare and what it costs in Zirakpur & Panchkula.
          </p>
        </Reveal>
        <div className="mt-12 space-y-4">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={Math.min(i * 0.05, 0.3)}>
              <FaqItem item={f} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
